import { useState, useRef, useEffect } from "react";
import { Button, useDismiss } from "@jkast90/kast-common/ui";
import { TargetType, ReactionSummary } from "../../hooks/useReactions";
import styles from "./ReactionPicker.module.css";

const DEFAULT_EMOJIS = ["❤️", "👍", "😂", "😮", "😢", "🎉"];

interface ReactionPickerProps {
  targetType: TargetType;
  targetId: number;
  reactions: ReactionSummary[];
  currentUserId: number;
  onAddReaction: (emoji: string) => void;
  onRemoveReaction: (emoji: string) => void;
  compact?: boolean;
}

export default function ReactionPicker({
  reactions,
  currentUserId,
  onAddReaction,
  onRemoveReaction,
  compact = false,
}: ReactionPickerProps) {
  const [showPicker, setShowPicker] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customEmoji, setCustomEmoji] = useState("");
  const pickerRef = useRef<HTMLDivElement>(null);
  const customInputRef = useRef<HTMLInputElement>(null);

  // Close picker when clicking outside
  useDismiss(showPicker, pickerRef, () => {
    setShowPicker(false);
    setShowCustomInput(false);
    setCustomEmoji("");
  });

  // Focus custom input when shown
  useEffect(() => {
    if (showCustomInput && customInputRef.current) {
      customInputRef.current.focus();
    }
  }, [showCustomInput]);

  const handleEmojiClick = (emoji: string) => {
    if (hasUserReacted(emoji)) {
      onRemoveReaction(emoji);
    } else {
      onAddReaction(emoji);
    }
    setShowPicker(false);
    setShowCustomInput(false);
    setCustomEmoji("");
  };

  const handleCustomEmojiSubmit = () => {
    const trimmed = customEmoji.trim();
    if (trimmed) {
      onAddReaction(trimmed);
      setShowPicker(false);
      setShowCustomInput(false);
      setCustomEmoji("");
    }
  };

  const handleCustomInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCustomEmojiSubmit();
    } else if (e.key === "Escape") {
      setShowCustomInput(false);
      setCustomEmoji("");
    }
  };

  const handleExistingReactionClick = (emoji: string) => {
    if (hasUserReacted(emoji)) {
      onRemoveReaction(emoji);
    } else {
      onAddReaction(emoji);
    }
  };

  // Check if current user has reacted with a specific emoji
  const hasUserReacted = (emoji: string): boolean => {
    const reaction = reactions.find((r) => r.emoji === emoji);
    return reaction?.user_ids.includes(currentUserId) ?? false;
  };

  return (
    <div
      className={`${styles.reactionContainer} ${compact ? styles.compact : ""}`}
      ref={pickerRef}
    >
      {/* Display existing reactions */}
      <div className={styles.reactions}>
        {reactions.map((reaction) => (
          <Button
            key={reaction.emoji}
            size="sm"
            className={`${styles.reactionBadge} ${hasUserReacted(reaction.emoji) ? styles.userReacted : ""}`}
            onClick={() => handleExistingReactionClick(reaction.emoji)}
            title={`${reaction.count} reaction${reaction.count !== 1 ? "s" : ""}`}
          >
            <span className={styles.emoji}>{reaction.emoji}</span>
            <span className={styles.count}>{reaction.count}</span>
          </Button>
        ))}

        {/* Add reaction button */}
        <Button
          size="sm"
          className={styles.addReactionButton}
          onClick={() => setShowPicker(!showPicker)}
          title="Add reaction"
        >
          <span className={styles.addIcon}>+</span>
          <span className={styles.addEmoji}>😊</span>
        </Button>
      </div>

      {/* Emoji picker dropdown */}
      {showPicker && (
        <div className={styles.picker}>
          {DEFAULT_EMOJIS.map((emoji) => (
            <Button
              key={emoji}
              size="sm"
              className={`${styles.emojiOption} ${hasUserReacted(emoji) ? styles.selected : ""}`}
              onClick={() => handleEmojiClick(emoji)}
            >
              {emoji}
            </Button>
          ))}
          {!showCustomInput ? (
            <Button
              size="sm"
              className={styles.moreButton}
              onClick={() => setShowCustomInput(true)}
              title="Pick any emoji"
            >
              +
            </Button>
          ) : (
            <div className={styles.customInputWrapper}>
              <input
                ref={customInputRef}
                type="text"
                className={styles.customInput}
                value={customEmoji}
                onChange={(e) => setCustomEmoji(e.target.value)}
                onKeyDown={handleCustomInputKeyDown}
                placeholder="🎸"
                maxLength={4}
              />
              <Button
                size="sm"
                className={styles.customSubmit}
                onClick={handleCustomEmojiSubmit}
                disabled={!customEmoji.trim()}
              >
                ✓
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
